/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

'use client';

import { useEffect, useState } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { request } from '@/services/request';
import { Component } from 'types/api-data';

import { cn } from '@/lib/utils';
import { CustomButton } from '@/components/common';
import { ConfiguredComponentsCard } from '@/components/components-page/configured-components-card';
import { DetailsPageSpinner } from '@/components/spinner/details-page-spinner';

type Status = 'loading' | 'discovering' | 'found' | 'empty';

// fetch logical components and keep only workflow types
const fetchWorkflows = async (): Promise<Component[]> => {
  const res = await request.get({
    endpoint: 'components',
    queryParams: { domain: 'logical' },
  });
  const workflows = (res[0]?.components || []).filter((component: Component) =>
    component.type?.startsWith('workflow')
  );
  return workflows.slice(0, 8); // display upto 8 workflows
};

export const WorkflowDiscoverySection = ({
  showExamples,
}: {
  showExamples: boolean;
}) => {
  const { messages } = useLanguage();
  const langData = messages?.DashboardPage;

  const [status, setStatus] = useState<Status>('loading');
  const [workflows, setWorkflows] = useState<Component[]>([]);

  // On mount, check for existing workflows.
  useEffect(() => {
    (async () => {
      try {
        let found = await fetchWorkflows();
        // if no workflows found, trigger discovery and re-fetch.
        if (found.length === 0) {
          setStatus('discovering');
          await request.put({ endpoint: 'discover/workflows' });
          found = await fetchWorkflows();
        }
        setWorkflows(found);
        setStatus(found.length ? 'found' : 'empty');
      } catch (error) {
        console.error('Workflow discovery failed:', error);
        setStatus('empty');
      }
    })();
  }, []);

  // on initial fetch, checking for existing workflows
  if (status === 'loading') {
    return (
      <div className="my-[100px] flex flex-col items-center text-center">
        <DetailsPageSpinner />
      </div>
    );
  }

  // State : no workflow(s) found – discovering
  if (status === 'discovering') {
    return (
      <div className="flex flex-col items-center text-center">
        <DetailsPageSpinner />
        <h3 className="my-4 text-h3 text-body-primary">
          {langData?.discovering_workflows}
        </h3>
        <p className="max-w-xl text-form-input-text text-form-input-disabled">
          {langData?.discovering_workflows_desc}
        </p>
      </div>
    );
  }

  // State : no workflow(s) Found
  if (status === 'empty') {
    return (
      <div className="flex flex-col items-center text-center">
        <h3 className="text-h3 text-body-primary">
          {langData?.send_traces_title}
        </h3>
        <p className="my-4 max-w-xl text-form-input-text text-form-input-disabled">
          {langData?.send_traces_desc}
        </p>
        <CustomButton
          customVariant="secondary"
          customSize="large"
          onClick={() => window.open('https://docs.okahu.ai/', '_blank')}
        >
          {langData?.learn_to_send_traces}
        </CustomButton>
      </div>
    );
  }

  // State : workflow(s) found
  return (
    <div>
      <h3 className="text-center text-h3 text-body-primary">
        {langData?.explore_traces_title}
      </h3>
      <div
        className={cn(
          'mt-4 grid grid-cols-1 gap-3',
          showExamples ? 'md:grid-cols-2' : 'md:grid-cols-3'
        )}
      >
        {workflows.map((component) => (
          <ConfiguredComponentsCard
            key={component.component_name}
            component={component}
          />
        ))}
      </div>
    </div>
  );
};
